import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Gavel as GavelIcon,
  VolunteerActivism as VolunteerIcon,
  Restaurant as RestaurantIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import hackathonService from '../../services/hackathonService';

// Food & Beverage Options
const LUNCH_OPTIONS = [
  {
    key: 'veg',
    label: 'Veg',
    items: ['Plain Rice', 'Chapati', 'Dry Sabji', 'Dal Curry', 'Curd', 'Salad', 'One Sweet']
  },
  {
    key: 'non_veg',
    label: 'Non-Veg',
    items: ['Rice', 'Roti', 'Dry Sabji', 'Chicken Curry', 'Curd', 'Salad (Veg)', 'One Sweet']
  }
];

const SNACK_OPTIONS = [
  { key: 'vada_pav', label: 'Vada Pav' },
  { key: 'veg_burger', label: 'Veg Burger' },
  { key: 'veg_cheese_burger', label: 'Veg Cheese Burger' },
  { key: 'egg_burger', label: 'Egg Burger' },
  { key: 'french_fries', label: 'French Fries' },
  { key: 'veg_nuggets', label: 'Veg Nuggets (6 pcs)' },
  { key: 'cheese_nuggets', label: 'Cheese Nuggets (6 pcs)' }
];

const JUICE_CATEGORY_OPTIONS = [
  { key: 'fresh_fruit', label: 'Fresh Fruit Juice' },
  { key: 'special_lime', label: 'Special Lime Juices' }
];

const FRESH_FRUIT_OPTIONS = [
  { key: 'mosambi', label: 'Mosambi' },
  { key: 'orange', label: 'Orange' },
  { key: 'grapes', label: 'Grapes' },
  { key: 'watermelon', label: 'Watermelon' },
  { key: 'muskmelon', label: 'Muskmelon' },
  { key: 'pineapple', label: 'Pineapple' },
  { key: 'papaya', label: 'Papaya' },
  { key: 'mix_fruit', label: 'Mix Fruit Juice' },
  { key: 'arabian_grapes', label: 'Arabian Grapes' },
  { key: 'pomegranate', label: 'Pomegranate' }
];

const SPECIAL_LIME_OPTIONS = [
  { key: 'fresh_lime', label: 'Fresh Lime' },
  { key: 'ginger_lime', label: 'Ginger Lime' },
  { key: 'mint_lime', label: 'Mint Lime' },
  { key: 'kokum_lime', label: 'Kokum Lime' }
];

// Preferred Task Area Options (Volunteers only)
const TASK_AREA_OPTIONS = [
  {
    key: 'media_documentation',
    label: '📸 Media & Documentation',
    details: [
      'Capture pictures and videos',
      'Share post-hackathon highlights'
    ]
  },
  {
    key: 'food_refreshments',
    label: '🍕 Food & Refreshments',
    details: [
      'Coordinate meals, snacks, and tea/coffee breaks',
      'Ensure water and soft drinks availability',
      'Overall refreshments arrangement'
    ]
  },
  {
    key: 'recognition_awards',
    label: '🏆 Recognition & Awards',
    details: [
      'Trophy arrangement',
      'Certificate arrangement',
      'Medal arrangement'
    ]
  },
  {
    key: 'event_coordination',
    label: '🎯 Event Coordination',
    details: [
      'Kick-off coordination and daily agenda tracking',
      'Moderator for start/kick-off on Day 1 and closure on Day 2',
      'Create a Saturday–Sunday run-of-show plan',
      'Ensure timelines are followed (start, reviews, demos, closure)'
    ]
  },
  {
    key: 'participant_support',
    label: '🙋 Participant Support',
    details: [
      'Point of contact for participants',
      'Answer participant queries (logins, access, rules)',
      'Coordinate with Tech Support for quick resolution'
    ]
  }
];

const VolunteerRegistration = () => {
  const { volunteerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [volunteer, setVolunteer] = useState(null);
  const [event, setEvent] = useState(null);
  const [activeDay, setActiveDay] = useState(0);
  const [foodPreferences, setFoodPreferences] = useState([]);
  const foodPreferencesRef = useRef(null);

  const [formData, setFormData] = useState({
    work_mode: 'WFO',
    available_day1: true,
    available_day2: true,
    wants_to_participate: 'yes',
    preferred_task_area: 'media_documentation'
  });

  useEffect(() => {
    loadVolunteer();
  }, [volunteerId]);

  const loadVolunteer = async () => {
    try {
      setLoading(true);
      const response = await hackathonService.getPublicVolunteer(volunteerId);
      setVolunteer(response.volunteer);
      setEvent(response.event);

      // Pre-fill form with existing data
      setFormData({
        work_mode: response.volunteer.work_mode || 'WFO',
        available_day1: response.volunteer.available_day1 !== false,
        available_day2: response.volunteer.available_day2 !== false,
        wants_to_participate: response.volunteer.wants_to_participate || 'yes',
        preferred_task_area: response.volunteer.preferred_task_area || 'media_documentation'
      });

      // Initialize food preferences based on event dates
      // Use date-only comparison to avoid timezone issues
      const startDate = new Date(response.event.start_date);
      const endDate = new Date(response.event.end_date);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      const daysDiff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      const initialPreferences = Array.from({ length: daysDiff }, (_, index) => {
        const date = new Date(startDate);
        date.setDate(date.getDate() + index);
        return {
          day: index + 1,
          date: date.toISOString().split('T')[0],
          lunch: { key: LUNCH_OPTIONS[0].key, label: LUNCH_OPTIONS[0].label },
          snack: { key: SNACK_OPTIONS[0].key, label: SNACK_OPTIONS[0].label },
          juice_category: { key: JUICE_CATEGORY_OPTIONS[0].key, label: JUICE_CATEGORY_OPTIONS[0].label },
          juice_item: { key: FRESH_FRUIT_OPTIONS[0].key, label: FRESH_FRUIT_OPTIONS[0].label }
        };
      });

      // Try to load existing food preferences
      try {
        const foodResponse = await hackathonService.getVolunteerFoodPreferences(volunteerId);
        if (foodResponse.preferences && foodResponse.preferences.length > 0) {
          setFoodPreferences(foodResponse.preferences);
        } else {
          setFoodPreferences(initialPreferences);
        }
      } catch {
        setFoodPreferences(initialPreferences);
      }
    } catch (err) {
      setError('Failed to load registration. Please check the link or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.checked }));
  };

  const handleFoodPreferenceChange = (dayIndex, field, option) => {
    setFoodPreferences(prev => {
      const updated = [...prev];
      updated[dayIndex] = {
        ...updated[dayIndex],
        [field]: { key: option.key, label: option.label }
      };
      // Set first juice_item when juice_category changes
      if (field === 'juice_category') {
        if (option.key === 'fresh_fruit') {
          updated[dayIndex].juice_item = { key: FRESH_FRUIT_OPTIONS[0].key, label: FRESH_FRUIT_OPTIONS[0].label };
        } else if (option.key === 'special_lime') {
          updated[dayIndex].juice_item = { key: SPECIAL_LIME_OPTIONS[0].key, label: SPECIAL_LIME_OPTIONS[0].label };
        }
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      await hackathonService.submitPublicVolunteerRegistration(volunteerId, formData);

      // Save food preferences if participating
      if (formData.wants_to_participate === 'yes' && foodPreferences.length > 0) {
        await hackathonService.saveVolunteerFoodPreferences(volunteerId, foodPreferences);
      }

      setSuccess(true);
    } catch (err) {
      setError('Failed to submit registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isJury = volunteer?.volunteer_type === 'jury';
  const roleTitle = isJury ? 'Jury Member' : 'Volunteer';
  const RoleIcon = isJury ? GavelIcon : VolunteerIcon;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !volunteer) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (success) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
        <Card sx={{ maxWidth: 500, textAlign: 'center' }}>
          <CardContent>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Registration Confirmed!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Thank you for confirming your participation as {roleTitle} for {event?.name}.
            </Typography>
            <Box mt={3} display="flex" justifyContent="center" gap={1}>
              <Chip
                icon={<RoleIcon />}
                label={roleTitle}
                color="primary"
              />
              <Chip
                icon={<EventIcon />}
                label={`${new Date(event?.start_date).toLocaleDateString()} - ${new Date(event?.end_date).toLocaleDateString()}`}
                color="primary"
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (volunteer?.registration_status === 'confirmed') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
        <Card sx={{ maxWidth: 500, textAlign: 'center' }}>
          <CardContent>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Already Registered!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You have already confirmed your participation as {roleTitle} for {event?.name}.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        py: 4,
        px: 2,
        minHeight: '100vh'
      }}
    >
      <Box maxWidth={700} mx="auto">
        {/* Header */}
        <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Typography variant="h1" gutterBottom sx={{
              textAlign: 'center',
              color: '#fff',
              fontSize: '32px'
            }}>
              {event?.name}
            </Typography>
            <Box mt={2} display="flex" gap={2} sx={{ justifyContent: 'center' }}>
              <Chip
                icon={<EventIcon />}
                label={`${new Date(event?.start_date).toLocaleDateString()} - ${new Date(event?.end_date).toLocaleDateString()}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        )}

        <Box sx={{
          maxHeight: 'calc(100vh - 300px)',
          overflow: 'auto',
          mb: 5
        }}>
          {/* Volunteer Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PersonIcon color="primary" />
                <Typography variant="h6">{roleTitle} Information</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={volunteer?.name || ''}
                    disabled
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={volunteer?.email || ''}
                    disabled
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Role"
                    value={roleTitle}
                    disabled
                    variant="outlined"
                  />
                </Grid>
                {volunteer?.assigned_role && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Assigned Role"
                      value={volunteer.assigned_role.replace('_', ' ')}
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Work Mode</FormLabel>
                    <RadioGroup
                      row
                      value={formData.work_mode}
                      onChange={(e) => setFormData(prev => ({ ...prev, work_mode: e.target.value }))}
                    >
                      <FormControlLabel
                        value="WFO"
                        control={<Radio color="primary" />}
                        label="Work from Office"
                      />
                      <FormControlLabel
                        value="WFH"
                        control={<Radio color="primary" />}
                        label="Work from Home"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Responsibilities */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <RoleIcon color="primary" />
                <Typography variant="h6">Your Responsibilities</Typography>
              </Box>
              {isJury ? (
                <Box component="ul" sx={{ pl: 2 }}>
                  <li>Evaluate team presentations and demos</li>
                  <li>Score teams based on defined KPIs</li>
                  <li>Provide constructive feedback to participants</li>
                  <li>Participate in final deliberations</li>
                </Box>
              ) : (
                <Box component="ul" sx={{ pl: 2 }}>
                  <li>Assist with event logistics and coordination</li>
                  <li>Support participants during the hackathon</li>
                  <li>Help maintain event schedule</li>
                  <li>Coordinate with organizers and teams</li>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Preferred Task Area - Volunteers Only */}
          {!isJury && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <AssignmentIcon color="primary" />
                  <Typography variant="h6">Preferred Task Area</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Please select your preferred area of contribution during the hackathon.
                </Typography>

                <FormControl component="fieldset">
                  <RadioGroup
                    value={formData.preferred_task_area}
                    onChange={(e) => setFormData(prev => ({ ...prev, preferred_task_area: e.target.value }))}
                  >
                    {TASK_AREA_OPTIONS.map(option => (
                      <Box key={option.key}>
                        <FormControlLabel
                          value={option.key}
                          control={<Radio color="primary" />}
                          label={option.label}
                        />
                        {formData.preferred_task_area === option.key && (
                          <Box sx={{ ml: 4, mb: 1 }}>
                            {option.details.map((detail, idx) => (
                              <Typography key={idx} variant="body2" color="text.secondary">
                                → {detail}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </RadioGroup>
                </FormControl>
              </CardContent>
            </Card>
          )}

          {/* Availability */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <EventIcon color="primary" />
                <Typography variant="h6">Availability</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Please confirm your availability for the hackathon days.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.available_day1}
                    onChange={handleCheckboxChange('available_day1')}
                    color="primary"
                  />
                }
                label={`Day 1 - ${new Date(event?.start_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`}
              />
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.available_day2}
                    onChange={handleCheckboxChange('available_day2')}
                    color="primary"
                  />
                }
                label={`Day 2 - ${new Date(event?.end_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`}
              />
            </CardContent>
          </Card>

          {/* Confirm Your Participation */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CheckCircleIcon color="primary" />
                <Typography variant="h6">Confirm Your Participation</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Please confirm if you would like to participate as {roleTitle} in this hackathon.
              </Typography>

              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={formData.wants_to_participate}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, wants_to_participate: value }));
                    if (value === 'yes') {
                      setTimeout(() => {
                        foodPreferencesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }
                  }}
                >
                  <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes, I want to participate" />
                  <FormControlLabel value="no" control={<Radio color="primary" />} label="No, I cannot participate" />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>

          {/* Food & Beverage Preferences - Only show when participating */}
          {formData.wants_to_participate === 'yes' && foodPreferences.length > 0 && (
            <Card ref={foodPreferencesRef} sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <RestaurantIcon color="primary" />
                  <Typography variant="h6">Food & Beverage Preferences</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Please select your food and beverage preferences for each day.
                </Typography>

                <Tabs
                  value={activeDay}
                  onChange={(e, newValue) => setActiveDay(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                >
                  {foodPreferences.map((pref, index) => {
                    // Calculate date from event start_date + day offset (same as Availability section)
                    const dateObj = new Date(event?.start_date);
                    dateObj.setDate(dateObj.getDate() + index);
                    return (
                      <Tab
                        key={index}
                        label={`Day ${pref.day} - ${dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
                      />
                    );
                  })}
                </Tabs>

                {foodPreferences.map((pref, dayIndex) => (
                  <Box key={dayIndex} hidden={activeDay !== dayIndex}>
                    {/* Lunch Section */}
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Lunch
                      </Typography>
                      <FormControl component="fieldset">
                        <RadioGroup
                          value={pref.lunch.key}
                          onChange={(e) => {
                            const option = LUNCH_OPTIONS.find(o => o.key === e.target.value);
                            handleFoodPreferenceChange(dayIndex, 'lunch', option);
                          }}
                        >
                          {LUNCH_OPTIONS.map(option => (
                            <Box key={option.key}>
                              <FormControlLabel
                                value={option.key}
                                control={<Radio color="primary" />}
                                label={option.label}
                              />
                              {pref.lunch.key === option.key && (
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
                                  → {option.items.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Snacks Section */}
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Snacks
                      </Typography>
                      <FormControl component="fieldset">
                        <RadioGroup
                          value={pref.snack.key}
                          onChange={(e) => {
                            const option = SNACK_OPTIONS.find(o => o.key === e.target.value);
                            handleFoodPreferenceChange(dayIndex, 'snack', option);
                          }}
                        >
                          <Grid container spacing={1}>
                            {SNACK_OPTIONS.map(option => (
                              <Grid item xs={12} sm={6} key={option.key}>
                                <FormControlLabel
                                  value={option.key}
                                  control={<Radio color="primary" />}
                                  label={option.label}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </FormControl>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Juice Section */}
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Juice
                      </Typography>
                      <FormControl component="fieldset">
                        <RadioGroup
                          value={pref.juice_category.key}
                          onChange={(e) => {
                            const option = JUICE_CATEGORY_OPTIONS.find(o => o.key === e.target.value);
                            handleFoodPreferenceChange(dayIndex, 'juice_category', option);
                          }}
                        >
                          {JUICE_CATEGORY_OPTIONS.map(option => (
                            <FormControlLabel
                              key={option.key}
                              value={option.key}
                              control={<Radio color="primary" />}
                              label={option.label}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>

                      {/* Fresh Fruit Juice Options */}
                      {pref.juice_category.key === 'fresh_fruit' && (
                        <Box sx={{ ml: 3, mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Select your juice:
                          </Typography>
                          <FormControl component="fieldset">
                            <RadioGroup
                              value={pref.juice_item.key}
                              onChange={(e) => {
                                const option = FRESH_FRUIT_OPTIONS.find(o => o.key === e.target.value);
                                handleFoodPreferenceChange(dayIndex, 'juice_item', option);
                              }}
                            >
                              <Grid container spacing={1}>
                                {FRESH_FRUIT_OPTIONS.map(option => (
                                  <Grid item xs={6} sm={4} key={option.key}>
                                    <FormControlLabel
                                      value={option.key}
                                      control={<Radio size="small" color="primary" />}
                                      label={option.label}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      )}

                      {/* Special Lime Juice Options */}
                      {pref.juice_category.key === 'special_lime' && (
                        <Box sx={{ ml: 3, mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Select your juice:
                          </Typography>
                          <FormControl component="fieldset">
                            <RadioGroup
                              value={pref.juice_item.key}
                              onChange={(e) => {
                                const option = SPECIAL_LIME_OPTIONS.find(o => o.key === e.target.value);
                                handleFoodPreferenceChange(dayIndex, 'juice_item', option);
                              }}
                            >
                              <Grid container spacing={1}>
                                {SPECIAL_LIME_OPTIONS.map(option => (
                                  <Grid item xs={6} sm={4} key={option.key}>
                                    <FormControlLabel
                                      value={option.key}
                                      control={<Radio size="small" color="primary" />}
                                      label={option.label}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

        </Box>

        {/* Submit Button */}
        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            size="large"
            color="primary"
            onClick={handleSubmit}
            disabled={submitting || (!formData.available_day1 && !formData.available_day2)}
            sx={{ minWidth: 250 }}
          >
            {submitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Confirm Registration'}
          </Button>
        </Box>

        {!formData.available_day1 && !formData.available_day2 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Please select at least one day of availability to confirm your participation.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default VolunteerRegistration;
